import React, { useState } from "react";
import { Card, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { supabase } from "../../config/Supabase";

const PAYOUT_METHODS = [
  { value: "paypal", label: "PayPal" },
  { value: "venmo", label: "Venmo" },
  { value: "zelle", label: "Zelle" },
  { value: "ach", label: "US Bank (ACH)" },
  { value: "stripe_connect", label: "Stripe Connect" },
];

const validatePayoutDetails = (method, details) => {
  switch (method) {
    case "paypal":
      if (!details.paypal_email) return "Please enter your PayPal email.";
      if (!/\S+@\S+\.\S+/.test(details.paypal_email))
        return "Please enter a valid PayPal email.";
      break;
    case "venmo":
      if (!details.venmo_handle)
        return "Please enter your Venmo username or phone number.";
      break;
    case "zelle":
      if (!details.zelle_contact)
        return "Please enter the email or phone number linked to your Zelle account.";
      break;
    case "ach":
      if (!details.bank_name) return "Please enter your bank name.";
      if (!details.account_holder) return "Please enter the account holder name.";
      if (!details.account_number) return "Please enter your account number.";
      if (!details.routing_number) return "Please enter your routing number.";
      if (!/^\d{9}$/.test(details.routing_number.replace(/\s/g, "")))
        return "Routing number must be 9 digits.";
      break;
    case "stripe_connect":
      if (!details.stripe_email)
        return "Please enter the email linked to your Stripe account.";
      break;
    default:
      break;
  }
  return null;
};

const AffiliateProfile = ({ affiliate, onUpdated }) => {
  const [editing, setEditing] = useState(!affiliate?.payout_method);
  const [displayName, setDisplayName] = useState(affiliate?.display_name || "");
  const [payoutMethod, setPayoutMethod] = useState(affiliate?.payout_method || "");
  const [details, setDetails] = useState(affiliate?.payout_details || {});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const setDetail = (key, value) =>
    setDetails((prev) => ({ ...prev, [key]: value }));

  const handleMethodChange = (val) => {
    setPayoutMethod(val);
    setDetails({});
    setFeedback(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (!payoutMethod) {
      setFeedback({ type: "danger", message: "Please select a payout method." });
      return;
    }
    const detailError = validatePayoutDetails(payoutMethod, details);
    if (detailError) {
      setFeedback({ type: "danger", message: detailError });
      return;
    }
    if (!displayName.trim()) {
      setFeedback({ type: "danger", message: "Please enter a display name." });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("affiliates")
        .update({
          display_name: displayName,
          payout_method: payoutMethod,
          payout_details: details,
        })
        .eq("id", affiliate.id);

      if (error) throw error;
      setFeedback({ type: "success", message: "Profile saved successfully." });
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Failed to update affiliate:", err);
      setFeedback({
        type: "danger",
        message: err.message || "Failed to save profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-sm" style={{ border: "none" }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ margin: 0 }}>Profile & Payout</h5>
          {!editing && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setEditing(true);
                setFeedback(null);
              }}
            >
              Edit
            </Button>
          )}
        </div>

        {!affiliate?.payout_method && !editing && (
          <Alert variant="warning" className="mb-3">
            Please add your payout details so we can send you your commissions.
          </Alert>
        )}

        <Form onSubmit={handleSave}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Display name</Form.Label>
                <Form.Control
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={!editing}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={affiliate?.email}
                  disabled
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Payout method <span style={{ color: "#a94442" }}>*</span>
                </Form.Label>
                <Form.Select
                  value={payoutMethod}
                  onChange={(e) => handleMethodChange(e.target.value)}
                  disabled={!editing}
                  required
                >
                  <option value="">Select a payout method…</option>
                  {PAYOUT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* PayPal */}
            {payoutMethod === "paypal" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    PayPal email <span style={{ color: "#a94442" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@paypal.com"
                    value={details.paypal_email || ""}
                    onChange={(e) => setDetail("paypal_email", e.target.value)}
                    disabled={!editing}
                    required
                  />
                </Form.Group>
              </Col>
            )}

            {/* Venmo */}
            {payoutMethod === "venmo" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Venmo username or phone{" "}
                    <span style={{ color: "#a94442" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="@username or phone number"
                    value={details.venmo_handle || ""}
                    onChange={(e) => setDetail("venmo_handle", e.target.value)}
                    disabled={!editing}
                    required
                  />
                </Form.Group>
              </Col>
            )}

            {/* Zelle */}
            {payoutMethod === "zelle" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Zelle email or phone{" "}
                    <span style={{ color: "#a94442" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Email or phone linked to Zelle"
                    value={details.zelle_contact || ""}
                    onChange={(e) => setDetail("zelle_contact", e.target.value)}
                    disabled={!editing}
                    required
                  />
                </Form.Group>
              </Col>
            )}

            {/* ACH */}
            {payoutMethod === "ach" && (
              <>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Bank name <span style={{ color: "#a94442" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Chase, Wells Fargo"
                      value={details.bank_name || ""}
                      onChange={(e) => setDetail("bank_name", e.target.value)}
                      disabled={!editing}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Account holder name{" "}
                      <span style={{ color: "#a94442" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Full name on the account"
                      value={details.account_holder || ""}
                      onChange={(e) =>
                        setDetail("account_holder", e.target.value)
                      }
                      disabled={!editing}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Account number{" "}
                      <span style={{ color: "#a94442" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Bank account number"
                      value={details.account_number || ""}
                      onChange={(e) =>
                        setDetail("account_number", e.target.value)
                      }
                      disabled={!editing}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Routing number (9 digits){" "}
                      <span style={{ color: "#a94442" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="9-digit routing number"
                      value={details.routing_number || ""}
                      onChange={(e) =>
                        setDetail("routing_number", e.target.value)
                      }
                      disabled={!editing}
                      required
                      maxLength={9}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            {/* Stripe Connect */}
            {payoutMethod === "stripe_connect" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Stripe account email{" "}
                    <span style={{ color: "#a94442" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@stripe.com"
                    value={details.stripe_email || ""}
                    onChange={(e) => setDetail("stripe_email", e.target.value)}
                    disabled={!editing}
                    required
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          {feedback && (
            <Alert
              variant={feedback.type}
              className="mt-3"
              onClose={() => setFeedback(null)}
              dismissible
            >
              {feedback.message}
            </Alert>
          )}

          {editing && (
            <div className="mt-3 d-flex justify-content-end gap-2">
              {affiliate?.payout_method && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    setDisplayName(affiliate.display_name || "");
                    setPayoutMethod(affiliate.payout_method || "");
                    setDetails(affiliate.payout_details || {});
                    setFeedback(null);
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving…" : "Save Profile"}
              </Button>
            </div>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AffiliateProfile;
